"use strict";

import Snackbar from "./FlashMessages";
import { FormInput } from "@/backoffice/components/form/components/input.js";

// noinspection TypeScriptUnresolvedReference
registerModule('fetch');

interface NetworkRequestOptions {
    method?: string,
    headers?: {},
    body?: Blob|BufferSource|FormData|URLSearchParams|ReadableStream,
    mode?: 'cors'|'no-cors'|'same-origin',
    credentials?: 'omit'|'same-origin'|'include',
    cache?: 'default'|'no-store'|'reload'|'no-cache'|'force-cache'|'only-if-cached',
    redirect?: 'follow'|'error'|'manual',
    referrer?: string,
    referrerPolicy?: 'no-referrer'|'no-referrer-when-downgrade'|'same-origin'|'origin'|'strict-origin'|'origin-when-cross-origin'|'strict-origin-when-cross-origin'|'unsafe-url',
    integrity?: string,
    keepalive?: boolean,
    target?: jQuery|HTMLElement,
    quiet?: boolean,
    validate?: boolean,
}

// noinspection TypeScriptUnresolvedReference
export class NetworkRequest {
    /**
     * Creates a new Network Request instance. It exposes methods of fetching and sending data; 'get', 'post' etc.
     * It uses the fetch api internally. These methods return a Promise, which resolves into a NetworkResponse. On
     * failure, they will throw a NetworkException object.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/fetch#parameters
     * @param {RequestInfo} url
     * @param {NetworkRequestOptions|RequestInit} [options]
     */
    constructor(url : string , options : NetworkRequestOptions|RequestInit = {}) {
        if (! url) {
            url = location.href;
        }

        this.url = url;
        this.options = options;
        this.quiet = !!options.quiet;
        this.validate = !!options.validate;

        this.body = null;
        this.headers = {
            // this is required to make the '$request->ajax()' work in PHP.
            'X-Requested-With': 'XMLHttpRequest',
            // But it's actually handled by...
            'X-Requested-By': 'NetworkRequest'
        };

        // Custom debug bar header.
        if (isModuleLoaded('debug-bar')) {
            this.headers['Debug-Bar'] = 'dev';
        }

        this.sent = false;
        this.pending = true;
    }

    /**
     * Sets up the headers, CSRF-tokens, abort signal and request body.
     *
     * @private
     */
    __prepareOptions() : void {
        const tokenMeta = document.querySelector('meta[name="_token"]');
        if (tokenMeta && (this.options.body || this.body)) {
            this.headers['X-CSRF-TOKEN'] = tokenMeta.getAttribute('content');
        }

        // Mapping the default headers first will allow the extra headers in options to overwrite them.
        this.options.headers = {...this.headers, ...this.options.headers};

        if (this.body) {
            this.options.body = this.body;
        }

        // Try setting an Abort Controller, but if it fails (due to old browser), don't throw any exception.
        try {
            this.abortSignal = new AbortController();
            this.options.signal = this.abortSignal.signal;
        } catch (e) {
            console.warn(e);
        }
    }

    /**
     * Enables Validation processing.
     *
     * @returns {NetworkRequest}
     */
    withValidation() : NetworkRequest {
        this.validate = true;

        return this;
    }

    /**
     * Appends the given data to the request's body.
     *
     * @param {FormData|string} object
     * @returns {NetworkRequest}
     */
    with(object : FormData|string) : NetworkRequest {
        this.body = object;

        return this;
    }

    /**
     * Takes the given object and encodes it as a JSON string. Also sets the content type header.
     *
     * @param {Object|Array|string|number} body
     * @returns {NetworkRequest}
     */
    withJson(body : any) : NetworkRequest {
        this.body = JSON.stringify(body);

        this.headers['Content-Type'] = 'application/json';

        return this;
    }

    /**
     * Takes the given Form element and parses it as a FormData.
     *
     * @param {HTMLFormElement} HTMLFormElement
     * @returns {NetworkRequest}
     */
    withForm(HTMLFormElement : HTMLFormElement) : NetworkRequest {
        this.body = new FormData(HTMLFormElement);

        return this;
    }

    /**
     * Makes a request to the given URL. If no method is previously set, it defaults to a GET request.
     *
     * @param {string} [method] Sets the method if it hasn't already been set (soft overwrite).
     * @returns {Promise<NetworkResponse>}
     * @throws {NetworkException}
     */
    call(method : string) : Promise<NetworkResponse> {
        this.options.method = this.options.method || method;
        this.__prepareOptions();
        this.sent = true;
        this.pending = true;

        return new Promise((resolve : Function, reject : Function) =>
            fetch(this.url, this.options)
                /**
                 * Parse the Response and check if the response was successful.
                 */
                .then((response : Response) => {
                    this.pending = false;
                    this.response = response;

                    if (response.opaque) {
                        return response;
                    }

                    if (! response.ok && !(this.validate && response.status === 422)) {
                        throw new NetworkException('Received non-ok result status (status: ' + response.status + ')', response, this.quiet);
                    }

                    return response;
                })
                /**
                 * Parse the response body and create a new NetworkResponse.
                 */
                .then(async (response : Response) => {
                    let body;

                    if (response.headers.has('content-type')) {
                        const contentType = response.headers.get('content-type');

                        if (contentType === 'application/json') {
                            body = await response.json();
                        } else if (contentType.startsWith('image/') || contentType === 'application/octet-stream') {
                            body = await response.blob();
                        }
                    }

                    if (! body) {
                        body = await response.text();
                    }

                    return new NetworkResponse(
                        response,
                        body
                    );
                })
                /**
                 * Handle custom features such as flash-messages and HTML-content setting.
                 */
                .then((result : NetworkResponse) => {
                    // Show the body inside an HTML element.
                    if (this.options.target) {
                        if (this.options.target instanceof jQuery) {
                            this.options.target.html(result.body.html);
                        } else if (this.options.target instanceof HTMLElement) {
                            this.options.target.innerHTML = result.body.html;
                        } else {
                            console.warn("Unable to set the content of", this.options.target, "as it's not a jQuery instance or HTMLElement");
                        }
                    }

                    if (result.body) {
                        // Show flash messages (assuming quiet isn't set)
                        if (! this.quiet && isModuleLoaded('flash-messages')) {
                            triggerFlashMessage(result.body);
                        }

                        // Handle validation exceptions
                        if (this.validate && result.status === 422) {
                            const errors = result.body.errors;

                            FormInput.handleErrors(errors);
                        }

                        // Handle debug bar data.
                        if (isModuleLoaded('debug-bar') && window.debugBar && result.body.debugBar) {
                            window.debugBar.addRequest(result.body.debugBar);
                        }
                    }

                    return result;
                })
                .then((result : NetworkResponse) => resolve(result))
                .catch(exception => exception instanceof NetworkException
                    ? reject(exception)
                    : reject(new NetworkException(exception, null, this.quiet))
                )
        );
    }

    /**
     * Calls the address using the POST method.
     *
     * @returns {Promise<NetworkResponse>}
     * @throws {NetworkException}
     */
    post() : Promise<NetworkResponse> {
        return this.call('POST');
    }

    /**
     * Calls the address with the default method (GET).
     *
     * @returns {Promise<NetworkResponse>}
     * @throws {NetworkException}
     */
    get() : Promise<NetworkResponse> {
        return this.call('GET');
    }

    /**
     * Calls the address using the DELETE method.
     *
     * @returns {Promise<NetworkResponse>}
     * @throws {NetworkException}
     */
    delete() : Promise<NetworkResponse> {
        // Cannot redirect delete requests.
        this.options.redirect = 'error';

        return this.call('DELETE');
    }

    /**
     * Calls the address using the PUT method.
     *
     * @returns {Promise<NetworkResponse>}
     * @throws {NetworkException}
     */
    put() : Promise<NetworkResponse> {
        // Cannot redirect put requests.
        this.options.redirect = 'error';

        return this.call('PUT');
    }

    /**
     * Calls the address using the PATCH method.
     *
     * @returns {Promise<NetworkResponse>}
     * @throws {NetworkException}
     */
    patch() : Promise<NetworkResponse> {
        // Cannot redirect patch requests.
        this.options.redirect = 'error';

        return this.call('PATCH');
    }

    /**
     * Cancels the current request. This will throw an exception on the NetworkRequest Promise.
     */
    abort() : void {
        if (this.sent && this.pending) {
            // Turn the quiet mode on so that the user doesn't get an error message.
            this.quiet = true;
            this.abortSignal.abort('The request was aborted');
        }
    }
}

/**
 * Exception object that may be thrown in a `Network.get` or `Network.post` call.
 *
 * @param {string} message
 * @param response
 * @param {boolean} [quiet]
 * @constructor
 */
export function NetworkException(message : string, response: Response, quiet : boolean = false) {
    this.message = message;
    this.response = response;
    this.name = 'NetworkException';

    this.toString = function() {
        return this.name + ': ' + this.message;
    }

    // @ts-ignore
    if (isModuleLoaded('flash-messages') && !quiet) {
        new Snackbar({ message: 'Er is een fout opgetreden. Excuus voor het ongemak.', class: 'error' });
    }
}


export class NetworkResponse {
    body: any;
    status: number;
    statusText: string;
    ok: boolean;
    url: string;
    redirected: boolean;
    headers: {[key: string]: string} = {};

    /**
     * Holds the objects from a `NetworkRequest.get` or `NetworkRequest.post` call.
     *
     * @param {Response} response
     * @param {string|object|array|blob} body
     */
    constructor(response : Response, body : any) {
        /**
         * The body of the response. Could be a string, parsed json or blob.
         * @type {string|object|array|blob|{success:boolean,html?:string,data?:any}}
         */
        this.body = body;

        /**
         * The status code.
         * @type {number}
         */
        this.status = response.status;

        /**
         * The status text.
         * @type {string}
         */
        this.statusText = response.statusText;

        /**
         * If the status code is in the 200-range.
         * @type {boolean}
         */
        this.ok = response.ok;

        /**
         * The final URL the request ended on.
         * @type {string}
         */
        this.url = response.url;

        /**
         * If the request was redirected.
         * @type {boolean}
         */
        this.redirected = response.redirected;

        /**
         * A list of all response headers.
         */
        response.headers.forEach((value, name) => {
            this.headers[name] = value;
        });
    }
}
